import React from 'react';
import { motion } from 'framer-motion';
import { useApp } from '../../context/AppContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiPackage, FiShoppingCart, FiTrendingUp, FiDollarSign, FiClock, FiCheckCircle } = FiIcons;

function PrisonOverview() {
  const { user, orders, inventory } = useApp();
  const prisonInventory = inventory[user.prisonId] || [];
  const prisonOrders = orders.filter(order => order.prisonId === user.prisonId);
  
  const todayOrders = prisonOrders.filter(order => {
    const orderDate = new Date(order.createdAt);
    const today = new Date();
    return orderDate.toDateString() === today.toDateString();
  });
  
  const pendingOrders = prisonOrders.filter(order => order.status === 'pending');
  const completedOrders = prisonOrders.filter(order => order.status === 'completed');
  const totalRevenue = prisonOrders.reduce((sum, order) => sum + order.total, 0);
  const lowStockItems = prisonInventory.filter(item => item.stock < 10);

  const stats = [
    {
      title: 'Today\'s Orders',
      value: todayOrders.length,
      icon: FiShoppingCart,
      color: 'text-blue-600',
      bg: 'bg-blue-100'
    },
    {
      title: 'Pending Orders',
      value: pendingOrders.length,
      icon: FiClock,
      color: 'text-yellow-600',
      bg: 'bg-yellow-100'
    },
    {
      title: 'Completed Orders',
      value: completedOrders.length,
      icon: FiCheckCircle,
      color: 'text-green-600',
      bg: 'bg-green-100'
    },
    {
      title: 'Total Revenue',
      value: `£${totalRevenue.toFixed(2)}`,
      icon: FiDollarSign,
      color: 'text-primary-600',
      bg: 'bg-primary-100'
    }
  ];

  return (
    <div className="max-w-7xl mx-auto p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-government-900 mb-2">
          Prison Dashboard
        </h1>
        <p className="text-government-600">
          Welcome back! Here's what's happening today.
        </p>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-government-600 mb-1">
                  {stat.title}
                </p>
                <p className="text-2xl font-bold text-government-900">
                  {stat.value}
                </p>
              </div>
              <div className={`p-3 rounded-lg ${stat.bg}`}>
                <SafeIcon icon={stat.icon} className={`w-6 h-6 ${stat.color}`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Orders */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <h3 className="text-xl font-bold text-government-900 mb-4">
            Recent Orders
          </h3>
          {todayOrders.length > 0 ? (
            <div className="space-y-4">
              {todayOrders.slice(0, 5).map((order) => (
                <div key={order.id} className="flex items-center justify-between p-3 bg-government-50 rounded-lg">
                  <div>
                    <p className="font-medium text-government-900">
                      Order #{order.id.substring(0, 8)}
                    </p>
                    <p className="text-sm text-government-600">
                      {order.items.length} items • £{order.total.toFixed(2)}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    order.status === 'pending' 
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {order.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-government-600">No orders today</p>
          )}
        </motion.div>

        {/* Low Stock Alert */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <h3 className="text-xl font-bold text-government-900 mb-4">
            Low Stock Alert
          </h3>
          {lowStockItems.length > 0 ? (
            <div className="space-y-4">
              {lowStockItems.slice(0, 5).map((item) => (
                <div key={item.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-10 h-10 object-cover rounded"
                    />
                    <div>
                      <p className="font-medium text-government-900">
                        {item.name}
                      </p>
                      <p className="text-sm text-government-600">
                        {item.category}
                      </p>
                    </div>
                  </div>
                  <span className="text-red-600 font-medium">
                    {item.stock} left
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-government-600">All items well stocked</p>
          )}
        </motion.div>
      </div>
    </div>
  );
}

export default PrisonOverview;